/**
 * Figma design tokens that can't be expressed as CSS custom properties or Tailwind colors.
 *
 * Color tokens live on `:root` in `globals.scss` (the `--design-*` block) and
 * Tailwind consumes them via `tailwind.config.ts` → `theme.extend.colors.design.*`.
 *
 * This file covers gradients and shadows — values that Tailwind can't map to
 * utility classes through CSS variables alone. They are used in inline `style`
 * props wherever the Figma design calls for gradient fills or elevation shadows.
 *
 * Source of truth: Figma file nAUOFYHxalXxqmCszHqRGm (AI Assistant Design)
 */

// ── Gradient Fills ──────────────────────────────────────────────────────────

export const GRADIENTS = {
  /** Register CTA: warm orange-gold sweep. Also appears in ExpertWidget, podcasts. */
  orange: 'linear-gradient(155deg, #FFD16F 38%, #FF865B 100%)',
  /** Watch Intro / send button: blue accent sweep. */
  blue: 'linear-gradient(132deg, #3D8BE8 38%, #89BEEC 100%)',
} as const;

// ── Elevation Shadows ───────────────────────────────────────────────────────

export const SHADOWS = {
  /** Course card, popover card — Figma "small" elevation. */
  card: '0 4px 8px -2px rgba(0,0,0,.1), 0 2px 4px -2px rgba(0,0,0,.06)',
  /** Input bar — Figma "2" elevation. */
  input: '0px 4px 24px 8px rgba(177,170,170,0.15)',
} as const;

// ── Derived Types ───────────────────────────────────────────────────────────

export type GradientName = keyof typeof GRADIENTS;
export type ShadowName = keyof typeof SHADOWS;

// ── V3 Design System (Sara v3 redesign) ─────────────────────────────────────
//
// The primitives under src/components/assistant/primitives/ are the first
// consumers. These are additive to the tokens above (GRADIENTS/SHADOWS stay
// untouched — they back the existing V2 tree) rather than a replacement, since
// V2 and V3 UI coexist during the rollout.

export const V3_COLORS = {
  orange: '#E36001',
  orangeHover: '#f26a0c',
  orangeActive: '#cf5600',
  ink: '#31364E',
  secondary: '#5b6072',
  body: '#6b7184',
  muted: '#8a7c68',
  faint: '#b3a996',
  label: '#c0b39d',
  surfaceWarm: '#FFFAF5',
  surfaceSidebar: '#FBF7F1',
  cardBorder: '#f0e7da',
  warmBorder: '#eadfce',
  warmBorderAlt: '#ece3d6',
  liveGreen: '#1F8A5B',
  liveGreenBg: '#E8F3EC',
  amber: '#E8A94E',
  amberText: '#9A7534',
  amberBg: '#FBF1E2',
  amberBorder: '#F0E0C7',
  /** Icon-tile fill on an amber card/ribbon — call-notice.tsx's WARNING
   *  CARD_STYLES.tileBg and model-busy-ribbon.tsx's EXHAUSTED icon tile
   *  share this one value; keep it a single source rather than two literals
   *  drifting apart. */
  amberTile: '#F6E4C4',
  endRed: '#D9483B',
  dateTileInPersonBg: '#FBF1E2',
  dateTileOnlineBg: '#EAF1FB',
  dateTileInPersonMonth: '#B0863C',
  dateTileOnlineMonth: '#5A7BA8',
  pinnedBorder: '#F3D9BE',
  pinnedBg: 'rgba(255,246,236,.9)',
  // ── Turn 16 · call-start notice family (call-start-notice.tsx) ──────────
  // amberSubtitle/amberChipText already exist as local literals in
  // call-notice.tsx and model-busy-ribbon.tsx (pre-Turn-16) — tokenized here
  // for this file's new 3rd call site rather than retrofitting those two
  // (out of this restyle's scope), per this file's own "2+ places" rule.
  amberSubtitle: '#a98a55',
  amberChipText: '#5f4718',
  /** Icon-circle fill on the SAND (PATIENCE-family) call-start notice —
   *  mic-wait (16a-alt) and the calm reconnecting variant (16c) both use it. */
  sandNoticeIconBg: '#ECE6DC',
  /** call-controls.tsx's Mute/CC "dimmed-disabled" chrome (16a: pre-live,
   *  nothing to mute/caption yet) — a literal color swap, not just an
   *  opacity fade over the live-state chrome, per the design's own mock. */
  callDimmedBg: '#F4F0EA',
  callDimmedBorder: '#e6ddcf',
  callDimmedIcon: '#b8ac98',
  callDimmedText: '#b3a894',
  // ── StandardCard (primitives/standard-card.tsx) tone system ────────────
  // Every handoff/feedback card (queue, no-agent-available, leave-a-message,
  // call-back, star-rating, confirmation, plus ContactSummaryCard) composes
  // the SAME shared shell and picks from this exact tone palette — tokenized
  // here (not left as literals in the one shell file) because by construction
  // every future consumer reuses these same values, unlike a typical
  // single-call-site literal.
  /** DEFAULT and WARN share this tile bg; only the icon stroke shifts warmer
   *  for WARN (see standardCardWarnIcon) — SUCCESS alone gets a distinct
   *  tile+icon pairing. */
  standardCardTileBg: '#FBEBD8',
  standardCardWarnIcon: '#D98A3D',
  standardCardSuccessBg: '#E7F3EA',
  /** Coincidentally the same hex as identity-avatar's local PRESENCE_DOT_COLOR
   *  — unrelated purpose (a presence dot vs. a tile icon), so kept as its own
   *  token rather than importing that one across files. */
  standardCardSuccessIcon: '#1FA068',
  standardCardEyebrow: '#B4966D',
  standardCardMeta: '#a99a83',
  /** NEUTRAL tone (tool-timeout-*.tsx family) — a calm stone tile, distinct
   *  from the warm peach DEFAULT/WARN tile and never red (red is reserved).
   *  Reads as "temporarily unavailable," not "waiting" (amber) or "error."
   *  Figma-exact values, not derived from any existing gray. */
  standardCardNeutralBg: '#EDEFF3',
  standardCardNeutralIcon: '#6B7086',
  /** Invalid-phone field treatment (tool-timeout-callback.tsx) — gentle
   *  amber, deliberately NOT the red/error treatment ContactInfoCard uses
   *  for its fields. `amber` (#E8A94E) above is an exact match for the
   *  border, so it's reused rather than redeclared here; bg/text have no
   *  existing match (amberBg/amberText are a different, slightly deeper
   *  amber tuned for pills/chips) so those two are new. */
  amberInvalidBg: '#FFF8EF',
  amberInvalidText: '#B0742E',
} as const;

/** Voice-orb radial gradients (OrbAvatar). */
export const ORB_GRADIENTS = {
  voice:
    'radial-gradient(circle at 50% 34%, #FFE8B6 0%, #FFB84F 38%, #FF7E3E 76%, #EC5F1C 100%)',
  chat: 'radial-gradient(circle at 50% 28%, #FFF4D8 0%, #FFCB63 34%, #FF8F42 70%, #E85D18 100%)',
  /** Turn 13 (13a "Sara's speaking") AGENT brighter overlay — cross-faded via
   *  opacity OVER the base `voice` gradient in call-orb.tsx, because gradient
   *  STOPS can't be CSS-transitioned directly (only solid colors interpolate);
   *  a second full-bleed disc that fades in is the standard workaround. */
  voiceBright:
    'radial-gradient(circle at 50% 34%, #FFEEC4 0%, #FFBC54 38%, #FF7E3E 76%, #EC5F1C 100%)',
} as const;

export const V3_SHADOWS = {
  cta: '0 10px 22px -12px rgba(227,96,1,.7)',
  pinnedCard: '0 12px 30px -22px rgba(227,96,1,.55)',
  panel: '0 18px 50px -22px rgba(49,54,78,.28)',
} as const;

/** [Turn 17, 17a/17d] CallScreen's full-overlay backgrounds — replaces the
 *  single shared gradient the overlay used pre-docking. Two distinct radial
 *  sweeps (not just one reused at different sizes): mobile's is taller/more
 *  vertical (120% 70% at 50% 12%, an extra `#FBF3E9` stop) to read correctly
 *  behind the centered pre-dock orb AND the docked top header; desktop's is
 *  the wider panel sweep (120% 120% at 34% 26%). Both share the call
 *  screen's overall warm-cream palette (#FFF7EC → #FDEEDB → ...) with the
 *  rest of the voice surface, so the individual hex stops aren't
 *  re-tokenized on their own — the two full gradient strings are what
 *  recurs (mobile CallScreen background + desktop CallScreen background). */
export const V3_CALL_OVERLAY_GRADIENTS = {
  mobile:
    'radial-gradient(120% 70% at 50% 12%, #FFF7EC 0%, #FDEEDB 46%, #FBF3E9 72%, #FBF7F1 100%)',
  desktop:
    'radial-gradient(120% 120% at 34% 26%, #FFF7EC 0%, #FDEEDB 56%, #F8E1CA 100%)',
} as const;

export type V3ColorName = keyof typeof V3_COLORS;
export type OrbGradientName = keyof typeof ORB_GRADIENTS;
export type V3ShadowName = keyof typeof V3_SHADOWS;

// ── Turn 13 · voice-call status-state colors ────────────────────────────────
//
// Shared between call-status-cluster.tsx (desktop, under the orb) and
// call-top-bar-status.tsx (mobile parity, 13d) — both render the SAME four
// state glyphs (idle dot, thinking dots, agent label) side by side with the
// live orb, so their label/dot colors are centralized here rather than
// duplicated per file. Ring/glow rgba values used only inside call-orb.tsx
// stay local to that file (single call site — not duplicated across files,
// so they don't meet the "2+ places" bar for this shared block).
export const T13_STATUS_COLORS = {
  /** IDLE — "Ready to listen": muted, inviting-in tone (13a). */
  idleLabel: '#9a8f7f',
  idleDot: '#c9b79c',
  /** THINKING — "Sara is thinking": warm-neutral label, orange wave dots (13a/13c-C). */
  thinkingLabel: '#8a7c68',
  thinkingDot: '#E36001',
  /** AGENT — "Sara is speaking": brighter than the old shared #8a7c68 label,
   *  matching the brightened speaking-state orb (13a). */
  agentLabel: '#C6541A',
  /** EXHAUSTED — "Resting a moment" (Turn 15, 15d): a muted, deliberately
   *  un-alarming tone — the orb itself dims/desaturates to carry the "not
   *  working right now" signal; this label just names it. Turn 16 (16c)
   *  reuses this exact tone for the failed-state short labels ("Can't hear
   *  you" / "Couldn't connect") — same "resting" register. */
  restingLabel: '#9a8f7f',
  /** CONNECTING/RECONNECTING (Turn 16, 16a/16c) — warm amber-brown, distinct
   *  from restingLabel's grayer tone. Shared by the "Connecting…" dotted
   *  label and the calm (dot-less) "Reconnecting…" short label. */
  connectingLabel: '#9a7c58',
  /** The three "Connecting…" blink dots (16a/16d) — see call-dot-blink in
   *  tailwind.config.ts. */
  connectingDot: '#C98B4A',
} as const;

// ── Shared interaction feel — "press" affordance ────────────────────────────
//
// Owner decision: every interactive control in the assistant tree gets the
// SAME press feel on click/tap. Values are lifted from ExpandingCapsule's
// pre-existing JS-driven press animation (the "+ New" capsule — the design
// reference everything else is standardized against), not separately
// guessed, so nothing feels inconsistent side by side.
//
// PRESSABLE is a Tailwind class fragment for the common case: a control with
// NO pre-existing transition-property utility (transition-colors,
// transition-all, ...). Merge it in via `cn(...)`. `motion-safe:` respects
// prefers-reduced-motion — a user who's asked for less motion gets no scale
// animation, not a broken one.
//
// GOTCHA — do not blindly append PRESSABLE to a control that already has
// `transition-colors` (or similar): `transition-property` is a single CSS
// property, so a second utility class targeting it doesn't merge with the
// first, it silently overrides it in the generated stylesheet (Tailwind
// places `transition-transform` after `transition-colors`, so the LATTER
// would lose — breaking the color fade for everyone WITHOUT reduced motion
// set, i.e. most users). Where a control already animates color, fold every
// transitioning property into ONE unconditional arbitrary list instead —
// e.g. `transition-[background-color,border-color,color,transform]` — and
// gate only the scale VALUE behind motion-safe:
// `motion-safe:active:scale-[0.96]` (see call-controls.tsx / filter-chips.tsx
// for worked examples). If a control already uses `transition-all`, just add
// `motion-safe:active:scale-[0.96]` — transform is already covered, and nothing
// here sanctions introducing a fresh `transition: all`.
//
// PRESSABLE_SCALE / PRESSABLE_DURATION_MS are the same two numbers in raw
// form, for the one component (ExpandingCapsule) that computes its press
// transform in JS/inline `style` rather than a static Tailwind class, so the
// feel can't silently drift between the two mechanisms. NOTE: Tailwind's JIT
// scanner needs literal class text in a scanned file (see tailwind.config.ts
// `content`) — PRESSABLE below is hand-written to match these numbers rather
// than interpolated from them; keep the two in sync if either changes.
export const PRESSABLE_SCALE = 0.96;
export const PRESSABLE_DURATION_MS = 160;
export const PRESSABLE =
  'motion-safe:transition-transform motion-safe:duration-[160ms] motion-safe:ease-out motion-safe:active:scale-[0.96]';

// ── Assistant prose ─────────────────────────────────────────────────────────
//
// Owner decision (FB21): ALL of Sara's prose renders bare on the canvas —
// no bubble. The RAG path (content/rag-answer-block.tsx, design 4a) set the
// look; message-block.tsx's assistant-text branch adopted it, and this
// constant is the single source of truth so the two paths can't drift.
// Full literal class string, hand-written (JIT scanner rule — same as
// PRESSABLE above).
export const ASSISTANT_PROSE =
  "font-['Work_Sans'] text-[15.5px] leading-[1.7] text-[#31364E] break-words";

// ── Turn 21 · Embedded panel ────────────────────────────────────────────────
//
// The embed surface (`/us-en/ai-assistant/embed`, mounted in a cross-origin
// iframe by the sara-embed loader). Eight additions, all embed-scoped —
// everything else reuses v3 unchanged: no new brand colours, no second orange.
//
// Two things here are load-bearing rather than cosmetic:
//
//  1. `gutter` is what makes the 400px column work. 400 − 16×2 = 368px of
//     content, and every compact component (course card especially) is
//     redlined against that 368, not against 400.
//  2. The launcher tokens describe markup we do NOT control the background
//     of — it sits on an arbitrary host page over photos, dark sections, or
//     white. `ring` + `shadow` are therefore a pair, never one or the other:
//     ink #31364E on the white ring is 9.7:1, which is what carries the
//     contrast; brand orange on an unknown photo cannot be guaranteed AA, so
//     the orb is decoration. The loader emits these as LITERAL values in
//     plain CSS — the host page has no custom properties, no Tailwind, and
//     none of our fonts.
export const EMBED = {
  /** Desktop panel box. Mobile ignores both and uses 100dvh. */
  panelW: 400,
  panelH: 620,
  /** Panel corner. Sits between card 16 and sheet 20. */
  radius: 18,
  /** Body side padding — the 368px content column derives from this. */
  gutter: 16,
  /** Host-page orb button. Above the 44px tap floor at every breakpoint. */
  launcherSize: 56,
  /** Deeper than panel elevation — it floats over unknown, possibly busy, hosts. */
  launcherShadow: '0 8px 24px -6px rgba(28,24,18,.38)',
  /** The white pill that guarantees contrast. Non-negotiable on photo hosts. */
  launcherRing: '#ffffff',
  launcherRingPad: 4,
  /** Behind the mobile sheet during entry, and behind the desktop tray. */
  scrim: 'rgba(49,54,78,.42)',
  /** Transcript left visible above an open tray — the thread never fully hides. */
  trayInsetTop: 26,
} as const;

// Panel elevation reuses V3_SHADOWS.panel; the launcher deliberately does not
// (see EMBED.launcherShadow above — a different surface with a different job).
export const EMBED_CONTENT_W = EMBED.panelW - EMBED.gutter * 2; // 368
